<template>
  <div class="history-record-card history-record-card-component">
    <div class="columns is-multiline">
      <div
        v-for="(historyRecord, index) in dataList"
        :key="`${historyRecord.Id}-${index}`"
        class="column"
        :class="cardSize"
      >
        <div class="timeline-container">
          <!-- Data inicial como marco da timeline -->
          <div
            v-if="historyRecord.InitialDate"
            class="timeline-date"
          >
            {{ historyRecord.InitialDate }}
          </div>

          <!-- Linha vertical da timeline -->
          <div class="timeline-line" />

          <!-- Marcador circular -->
          <div class="timeline-marker" />

          <!-- Card de histórico -->
          <div class="card">
            <p class="type">
              {{ historyRecord.HistoryRecordType?.Name || "-" }}
            </p>

            <!-- Exibe a data inicial e, se existir, a data final -->
            <p class="date">
              {{ historyRecord.formattedDate }}
            </p>

            <p
              class="description"
              @click="openPopup(historyRecord.HistoryRecordNotes)"
            >
              {{ descriptionSlice(historyRecord.HistoryRecordNotes) }}
            </p>

            <div class="box-buttons">
              <div class="buttons">
                <button
                  type="button"
                  class="button is-rounded default"
                  @click="clickEditEvent(historyRecord, index)"
                >
                  Editar
                </button>
                <button
                  type="button"
                  class="button is-rounded cancel"
                  :class="historyRecord.deleteLoading ? 'is-loading' : ''"
                  @click="clickDeleteEvent(historyRecord, index)"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ✅ NOVO BLOCO: POPUP -->
  <div
    v-if="showPopup"
    class="popup-overlay"
    @click.self="closePopup"
  >
    <div class="popup-content">
      <p class="popup-message">
        {{ popupMessage }}
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { FrontendHistoryRecord } from "@/models";

export default defineComponent({
  name: "HistoryRecordCard",
  props: {
    dataList: {
      type: Array as () => FrontendHistoryRecord[],
      required: true,
      default: () => []
    },
    extendedDescription: {
      type: Boolean,
      required: false,
      default: false
    },
    cardSize: {
      type: String,
      required: false,
      default:
        "is-12-mobile is-12-tablet is-12-desktop is-6-widescreen is-4-fullhd"
    }
  },
  emits: ["customClickEditEvent", "customClickDeleteEvent"],
  data() {
    return {
      //  NOVO: estados da popup
      showPopup: false,
      popupMessage: ""
    };
  },

  methods: {
    clickEditEvent(historyRecord: FrontendHistoryRecord, index: number): void {
      this.$emit("customClickEditEvent", historyRecord, index);
    },
    clickDeleteEvent(
      historyRecord: FrontendHistoryRecord,
      index: number
    ): void {
      this.$emit("customClickDeleteEvent", historyRecord, index);
    },
    descriptionSlice(historyRecordNotes: string | null | undefined): string {
      if (!historyRecordNotes) {
        return "";
      }

      // No need to replace line breaks with <br>
      const maxLength = this.extendedDescription ? 350 : 120;
      if (historyRecordNotes.length > maxLength) {
        return `${historyRecordNotes.substring(0, maxLength)}...`;
      }

      return historyRecordNotes;
    },
    formatDate(date: string | null | undefined): string {
      if (!date) return "";
      const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
      };
      return new Date(date).toLocaleDateString(undefined, options);
    },
    //  NOVOS MÉTODOS: abrir e fechar popup
    openPopup(message: string): void {
      this.popupMessage = message;
      this.showPopup = true;
    },
    closePopup(): void {
      this.showPopup = false;
    }
  }
});
</script>

<style scoped>
@import "~@assets/css/components/historyRecordCard.css";

/* Animação de entrada */
@keyframes popup-fade-zoom {
  0% {
    opacity: 0;
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(20, 20, 20, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background: white;
  padding: 32px;
  border-radius: 16px;
  width: 600px;              /* largura fixa */
  min-height: 200px;         /* altura mínima */
  max-height: 70vh;
  overflow-y: auto;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
  font-size: 16px;
  line-height: 1.7;
  color: #333;
  font-family: 'Segoe UI', sans-serif;
  transition: all 0.3s ease;
  border: 1px solid #ddd;

  /* animação */
  animation: popup-fade-zoom 0.25s ease-out;
}

.popup-message {
  white-space: pre-line; /* respeita quebras de linha do texto */
}

.description {
  cursor: pointer;
  text-decoration: none; /* remove sublinhado */
}
</style>